import type { Theme } from '@mui/material';
import { Paper, Typography, useTheme, Box, Stack } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { ChartsTooltipContainer, useItemTooltip } from '@mui/x-charts/ChartsTooltip';
import type { ReactNode } from 'react';
import { formatNumber, isEmptyArray, isNullOrUndefined, oz, raiseUnreachable } from '@vy/utils';
import { formatPrice } from '@vy/services';
import { DEFAULT_CURRENCY } from '@vy/types';

export const CHART_DATA_IDS = {
  PAID_PRICE_ID: 'data-id-paidPrice',
  OPEN_PRICE_ID: 'data-id-openPrice',
  PENDING_PRICE_ID: 'data-id-pendingPrice',
  OVERDUE_PRICE_ID: 'data-id-overduePrice',
} satisfies Record<string, `data-id-${keyof ReportsPageInvoiceStatusChartProps}`>;

const ChartDataIdsSchema = oz.nativeEnum(CHART_DATA_IDS);

export type ChartDataIdsType = oz.infer<typeof ChartDataIdsSchema>;

type ColorMapping = Record<ChartDataIdsType, string>;

type PieDataItem = {
  id: string;
  value: number;
  label: string;
  color: string;
};

type ReportsPageInvoiceStatusChartProps = {
  totalInvoicesCount: number;
  paidPrice?: number;
  openPrice?: number;
  pendingPrice: number;
  overduePrice: number;
  width?: number;
  height?: number;
  outerRadiusMultiplier?: number;
  colorMapping?: ColorMapping;
  centerContent?: ReactNode;
};

type PieTooltipContentProps = {
  pieData: PieDataItem[];
  totalInvoicesCount: number;
};

const PieTooltipContent: React.FC<PieTooltipContentProps> = ({ pieData, totalInvoicesCount }) => {
  const tooltipData = useItemTooltip<'pie'>();
  if (!tooltipData) return null;

  if (isEmptyArray(pieData)) {
    return null;
  }

  return (
    <Paper
      sx={{
        backgroundColor: '#fff',
        transform: 'translate(-50%, -120%)',
        minWidth: '254px',
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}>
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: "18px",
            fontWeight: 600,
            color: "#000"
          }}>
          {formatNumber(totalInvoicesCount)} Total invoices
        </Typography>
        {pieData.map((itemData) => {
          const idAsString = String(itemData.id);
          const parsedId = ChartDataIdsSchema.safeParse(idAsString);

          if (!parsedId.success) {
            return null;
          }

          const label = getChartDataHighlightLabel(parsedId.data);
          const color = itemData.color;

          return (
            <Box
              key={idAsString}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1
              }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  backgroundColor: color,
                  flexShrink: 0,
                }}
              />
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  flex: 1
                }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    lineHeight: "16px",
                    fontWeight: 500,
                    color: "#000"
                  }}>
                  {label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    lineHeight: "14px",
                    color: "#000"
                  }}>
                  {formatPrice(itemData.value, DEFAULT_CURRENCY, 0, 0)}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export const ReportsPageInvoiceStatusChart: React.FC<ReportsPageInvoiceStatusChartProps> = ({
  overduePrice,
  pendingPrice,
  width = 230,
  height = 230,
  outerRadiusMultiplier = 0.4,
  colorMapping,
  totalInvoicesCount,
  paidPrice,
  openPrice,
  centerContent,
}) => {
  const vyTheme = useTheme();

  const pieData: PieDataItem[] = [
    {
      id: CHART_DATA_IDS.PENDING_PRICE_ID,
      value: pendingPrice,
      label: 'Pending Payment',
      color:
        colorMapping?.[CHART_DATA_IDS.PENDING_PRICE_ID] ??
        getChartDataBaseColor(CHART_DATA_IDS.PENDING_PRICE_ID, vyTheme).light,
    },
    {
      id: CHART_DATA_IDS.OVERDUE_PRICE_ID,
      value: overduePrice,
      label: 'Overdue',
      color:
        colorMapping?.[CHART_DATA_IDS.OVERDUE_PRICE_ID] ??
        getChartDataBaseColor(CHART_DATA_IDS.OVERDUE_PRICE_ID, vyTheme).light,
    },
    ...(isNullOrUndefined(paidPrice)
      ? []
      : [
          {
            id: CHART_DATA_IDS.PAID_PRICE_ID,
            value: paidPrice,
            label: 'Paid',
            color:
              colorMapping?.[CHART_DATA_IDS.PAID_PRICE_ID] ??
              getChartDataBaseColor(CHART_DATA_IDS.PAID_PRICE_ID, vyTheme).light,
          },
        ]),
    ...(isNullOrUndefined(openPrice)
      ? []
      : [
          {
            id: CHART_DATA_IDS.OPEN_PRICE_ID,
            value: openPrice,
            label: 'Open',
            color:
              colorMapping?.[CHART_DATA_IDS.OPEN_PRICE_ID] ??
              getChartDataBaseColor(CHART_DATA_IDS.OPEN_PRICE_ID, vyTheme).light,
          },
        ]),
  ];

  const TooltipSlot: React.FC = () => (
    <ChartsTooltipContainer trigger="item">
      <PieTooltipContent pieData={pieData} totalInvoicesCount={totalInvoicesCount} />
    </ChartsTooltipContainer>
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: width,
        height: height
      }}>
      <PieChart
        width={width}
        height={height}
        series={[
          {
            data: pieData,
            innerRadius: width * 0.38,
            outerRadius: width * outerRadiusMultiplier,
            paddingAngle: 0.5,
            cornerRadius: 0,
            startAngle: 0,
            endAngle: 360,
            cx: width / 2,
            cy: height / 2,
          },
        ]}
        slots={{ tooltip: TooltipSlot }}
        hideLegend={true}
      />
      {centerContent && (
        <Box
          sx={{
            position: "absolute",
            top: "53%",
            left: "52%",
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            textAlign: 'center'
          }}>
          {centerContent}
        </Box>
      )}
    </Box>
  );
};

function getChartDataHighlightLabel(id: ChartDataIdsType) {
  switch (id) {
    case CHART_DATA_IDS.PAID_PRICE_ID:
      return 'Paid Invoices';
    case CHART_DATA_IDS.OPEN_PRICE_ID:
      return 'Open Invoices';
    case CHART_DATA_IDS.PENDING_PRICE_ID:
      return 'Pending Payment';
    case CHART_DATA_IDS.OVERDUE_PRICE_ID:
      return 'Overdue';
    default:
      return raiseUnreachable(id);
  }
}

function getChartDataBaseColor(id: ChartDataIdsType, theme: Theme) {
  switch (id) {
    case CHART_DATA_IDS.PAID_PRICE_ID:
      return theme.palette.success;
    case CHART_DATA_IDS.OPEN_PRICE_ID:
      return theme.palette.primary;
    case CHART_DATA_IDS.PENDING_PRICE_ID:
      return theme.palette.warning;
    case CHART_DATA_IDS.OVERDUE_PRICE_ID:
      return theme.palette.error;
    default:
      return raiseUnreachable(id);
  }
}
